import { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/integrations/supabase/client'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { User, Mail, Phone, Calendar, Shield, Settings, Lock, Bell, Smartphone } from 'lucide-react'
import { format } from 'date-fns'
import { de } from 'date-fns/locale'
import { NotificationSettings } from '@/components/pwa/NotificationSettings'
import { WalletButtons } from '@/components/wallet/WalletButtons'

interface CustomerProfile {
  id: string
  email: string
  full_name: string
  phone?: string
  created_at: string
  visit_stamps: number
  referral_stamps: number
  total_visits: number
  total_referrals: number
  referral_code: string
  last_visit?: string
  points: number
}

const CustomerProfile = () => {
  const { user } = useAuth()
  const { toast } = useToast()
  const [profile, setProfile] = useState<CustomerProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    full_name: '',
    phone: ''
  })
  const [preferences, setPreferences] = useState({
    email_notifications: true,
    sms_notifications: false
  })

  useEffect(() => {
    fetchProfile()
  }, [user])

  const fetchProfile = async () => {
    if (!user?.id) return

    try {
      const fullName = `${user.user_metadata?.first_name || ''} ${user.user_metadata?.last_name || ''}`.trim() || 'Kunde'
      
      // Use the new secure RPC function
      const { data, error } = await supabase.rpc('get_or_create_customer_profile', {
        p_user_id: user.id,
        p_email: user.email || '',
        p_full_name: fullName
      })

      if (error) throw error

      if (data && data.length > 0) {
        const customerData = data[0]
        setProfile(customerData)
        setFormData({
          full_name: customerData.full_name || '',
          phone: customerData.phone || ''
        })
      } else {
        throw new Error('Failed to get or create customer profile')
      }
    } catch (error) {
      console.error('Error fetching profile:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Profil konnte nicht geladen werden.",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!profile) return

    setSaving(true)
    try {
      const { error } = await supabase
        .from('customers')
        .update({
          full_name: formData.full_name,
          phone: formData.phone
        } as any)
        .eq('id', profile.id)

      if (error) throw error

      toast({
        title: "Profil gespeichert",
        description: "Ihre Änderungen wurden erfolgreich gespeichert.",
      })

      await fetchProfile()
    } catch (error) {
      console.error('Error saving profile:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Profil konnte nicht gespeichert werden.",
      })
    } finally {
      setSaving(false)
    }
  }

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const generateNewReferralCode = async () => {
    if (!profile) return

    const newCode = Math.random().toString(36).substring(2, 8).toUpperCase()

    try {
      const { error } = await supabase
        .from('customers')
        .update({ referral_code: newCode } as any)
        .eq('id', profile.id)

      if (error) throw error

      toast({
        title: "Neuer Empfehlungscode",
        description: "Ihr Empfehlungscode wurde erfolgreich erneuert.",
      })

      await fetchProfile()
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Empfehlungscode konnte nicht erneuert werden.",
      })
    }
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center">
            <p>Wird geladen...</p>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  if (!profile) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardHeader>
              <CardTitle>Profil nicht gefunden</CardTitle>
              <CardDescription>
                Ihr Kundenprofil konnte nicht gefunden werden.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Mein Profil</h1>
          <p className="text-muted-foreground">
            Verwalten Sie Ihre persönlichen Daten und Einstellungen.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile Information */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Persönliche Informationen
                </CardTitle>
                <CardDescription>
                  Bearbeiten Sie Ihre persönlichen Daten.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="full_name">Vollständiger Name</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => handleInputChange('full_name', e.target.value)}
                    placeholder="Ihr vollständiger Name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">E-Mail-Adresse</Label>
                  <Input
                    id="email"
                    value={profile.email}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">
                    Die E-Mail-Adresse kann nicht geändert werden.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Telefonnummer (optional)</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Ihre Telefonnummer"
                  />
                </div>

                <div className="pt-4">
                  <Button onClick={handleSave} disabled={saving} className="w-full">
                    {saving ? "Wird gespeichert..." : "Änderungen speichern"}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Account Settings */}
            <NotificationSettings />

            {/* Digital Wallet */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="h-5 w-5" />
                  Digitale Wallet
                </CardTitle>
                <CardDescription>
                  Fügen Sie Ihre Treuekarten zu Apple Wallet oder Google Pay hinzu.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Treuekarte für Besuche</Label>
                  <WalletButtons passType="visit" className="w-full" />
                  <p className="text-xs text-muted-foreground mt-2">
                    Ihre Besuchsstempel: {profile.visit_stamps}/10
                  </p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Empfehlungskarte</Label>
                  <WalletButtons passType="referral" className="w-full" />
                  <p className="text-xs text-muted-foreground mt-2">
                    Ihre Empfehlungsstempel: {profile.referral_stamps}
                  </p>
                </div>
                
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs text-muted-foreground">
                    💡 <strong>Tipp:</strong> Fügen Sie die Karten zu Ihrer Wallet hinzu, 
                    um sie immer griffbereit zu haben und automatische Updates zu erhalten.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Account Statistics & Settings */}
          <div className="space-y-6">
            {/* Account Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Konto-Statistiken
                </CardTitle>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Mitglied seit:</span>
                  <span className="font-medium">
                    {format(new Date(profile.created_at), 'MMM yyyy', { locale: de })}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Besuchsstempel:</span>
                  <span className="font-medium">{profile.visit_stamps}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Empfehlungen:</span>
                  <span className="font-medium">{profile.referral_stamps}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Gesamtbesuche:</span>
                  <span className="font-medium">{profile.total_visits}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Gesamtpunkte:</span>
                  <span className="font-medium">{profile.points}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Empfehlungen gesamt:</span>
                  <span className="font-medium">{profile.total_referrals}</span>
                </div>
                
                {profile.last_visit && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Letzter Besuch:</span>
                    <span className="font-medium">
                      {format(new Date(profile.last_visit), 'dd.MM.yyyy', { locale: de })}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Referral Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Empfehlungseinstellungen
                </CardTitle>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Ihr Empfehlungscode:</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 p-2 bg-muted rounded text-sm font-mono">
                      {profile.referral_code}
                    </code>
                  </div>
                </div>
                
                <Button 
                  onClick={generateNewReferralCode} 
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                >
                  Neuen Code generieren
                </Button>
                
                <p className="text-xs text-muted-foreground">
                  Durch das Generieren eines neuen Codes werden alle vorherigen Links ungültig.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}

export default CustomerProfile